import { useState } from 'react';
import { createPortal } from 'react-dom';
import { RotateCcw, X, AlertTriangle, Timer } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';
import { useNotificationSettings } from '../hooks/useNotificationSettings';
import { notificationSettingGroups, NotificationSettings } from '../types';
import { usePartner } from '@/features/partner';
import { useSubscription } from '@/features/subscription';
import { usePregnancyStore } from '@/store/pregnancyStore';

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function ToggleSwitch({ checked, onChange, disabled }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-pp-orange focus:ring-offset-2 focus:ring-offset-pp-dark',
        checked ? 'bg-pp-orange' : 'bg-white/20',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm',
          checked ? 'translate-x-6' : 'translate-x-1'
        )}
      />
    </button>
  );
}

interface SettingRowProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function SettingRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: SettingRowProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1 pr-4">
        <p className="text-sm font-medium text-white">{label}</p>
        {description && (
          <p className="text-xs text-white/50 mt-0.5">{description}</p>
        )}
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

interface SettingGroupProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

function SettingGroup({ title, description, children }: SettingGroupProps) {
  return (
    <div className="mb-6">
      <div className="px-1 py-2">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {description && (
          <p className="text-xs text-white/50 mt-0.5">{description}</p>
        )}
      </div>
      <div className="bg-white/10 rounded-[15px] px-4 divide-y divide-white/10">{children}</div>
    </div>
  );
}

// Confirmation Modal for disabling contraction timer notifications
interface DisableContractionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function DisableContractionModal({ isOpen, onClose, onConfirm }: DisableContractionModalProps) {
  const { t } = useTranslation('notifications');
  if (!isOpen) return null;

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal max-w-md">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h2>{t('disableModal.title')}</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            <div className="flex items-start gap-3 p-4 bg-red-500/10 rounded-[15px] border border-red-500/20">
              <Timer className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-white/80">
                <p className="font-medium text-white mb-1">{t('disableModal.importantNote')}</p>
                <p dangerouslySetInnerHTML={{ __html: t('disableModal.description') }} />
                <p className="mt-2">
                  {t('disableModal.missInfo')}
                </p>
                <ul className="list-disc list-inside mt-1 text-white/60">
                  <li>{t('disableModal.waterBreak')}</li>
                  <li>{t('disableModal.bleeding')}</li>
                  <li>{t('disableModal.otherEmergencies')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-[15px] bg-white/10 text-white font-medium hover:bg-white/15 transition-colors"
            >
              {t('disableModal.cancel')}
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-3 px-4 rounded-[15px] bg-red-500/80 text-white font-medium hover:bg-red-500 transition-colors"
            >
              {t('disableModal.confirm')}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

// Mapping from German group titles to i18n keys
const groupKeyMap: Record<string, string> = {
  'Partner': 'groups.partner',
  'Namen-Swipen': 'groups.nameSwiping',
  'Checklisten': 'groups.checklists',
  'Kalender': 'groups.calendar',
  'Erinnerungs-Zeitpunkte': 'groups.reminderTiming',
  'Schwangerschaft': 'groups.pregnancy',
  'Wehenzähler': 'groups.contractionTimer',
  'Schlafplan': 'groups.sleepPlan',
  'Medikamenten-Plan': 'groups.medicationPlan',
  'System': 'groups.system',
  'Baby': 'groups.baby',
  'App': 'groups.app',
  'Community': 'groups.community',
};

const groupDescriptionKeyMap: Record<string, string> = {
  'Partner': 'groupDescriptions.partner',
  'Namen-Swipen': 'groupDescriptions.nameSwiping',
  'Checklisten': 'groupDescriptions.checklists',
  'Kalender': 'groupDescriptions.calendar',
  'Erinnerungs-Zeitpunkte': 'groupDescriptions.reminderTiming',
  'Schwangerschaft': 'groupDescriptions.pregnancy',
  'Wehenzähler': 'groupDescriptions.contractionTimer',
  'Schlafplan': 'groupDescriptions.sleepPlan',
  'Medikamenten-Plan': 'groupDescriptions.medicationPlan',
  'System': 'groupDescriptions.system',
};

export function NotificationSettingsPage() {
  const { t } = useTranslation('notifications');
  const { settings, isLoading, isSaving, updateSetting, resetToDefaults } =
    useNotificationSettings();
  const { currentPregnancy } = usePregnancyStore();
  const { isConnected } = usePartner(currentPregnancy?.id ?? null);
  const { isPremium } = useSubscription();

  const [showDisableModal, setShowDisableModal] = useState(false);

  // Check if Wehenzähler group should be visible
  // Only show if partner is connected AND user has premium
  const showContractionTimerSettings = isConnected && isPremium;

  // Filter out Wehenzähler group if conditions not met
  const visibleSettingGroups = notificationSettingGroups.filter((group) => {
    if (group.title === 'Wehenzähler') {
      return showContractionTimerSettings;
    }
    return true;
  });

  const handleSettingChange = async (
    key: keyof Omit<NotificationSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
    value: boolean
  ) => {
    // Special handling for emergency notifications - show confirmation when disabling
    if (key === 'notify_contraction_emergency_event' && !value) {
      setShowDisableModal(true);
      return;
    }

    await updateSetting(key, value);
  };

  const handleConfirmDisableContraction = async () => {
    await updateSetting('notify_contraction_emergency_event', false);
    setShowDisableModal(false);
  };

  const handleReset = async () => {
    if (window.confirm(t('resetConfirm'))) {
      await resetToDefaults();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-white">{t('title')}</h1>
        <button
          onClick={handleReset}
          disabled={isSaving}
          className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-[10px] transition-colors disabled:opacity-50"
          title={t('resetToDefault')}
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      <p className="text-white/60 text-sm mb-4">
        {t('description')}
      </p>

      {/* Settings Groups */}
      <div className="mt-4">
        {visibleSettingGroups.map((group) => (
          <SettingGroup
            key={group.title}
            title={t(groupKeyMap[group.title] || group.title)}
            description={group.description ? t(groupDescriptionKeyMap[group.title] || group.title) : undefined}
          >
            {group.settings.map((setting) => (
              <SettingRow
                key={setting.key}
                label={t(`settingLabels.${setting.key}`, setting.label)}
                description={setting.description ? t(`settingDescriptions.${setting.key}`, setting.description) : undefined}
                checked={settings?.[setting.key] ?? true}
                onChange={(value) => handleSettingChange(setting.key, value)}
                disabled={isSaving}
              />
            ))}
          </SettingGroup>
        ))}
      </div>

      {/* Save Indicator */}
      {isSaving && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-md text-white text-sm rounded-full shadow-lg">
          {t('saving')}
        </div>
      )}

      {/* Disable Contraction Notifications Confirmation Modal */}
      <DisableContractionModal
        isOpen={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        onConfirm={handleConfirmDisableContraction}
      />
    </div>
  );
}
